import React, { useState } from 'react';
import { ArrowUpDownIcon, SearchIcon } from 'lucide-react';
import { LoanRow } from './LoanRow';
import type { Loan } from './LoanRow';
interface LoanTableProps {
  loans: Loan[];
  onMarkComplete: (loan: Loan) => void;
  onView: (loan: Loan) => void;
  showActions?: boolean;
  title?: string;
}
type SortField = 'borrowerName' | 'fundedDate' | 'loanType' | 'rate';
type SortDirection = 'asc' | 'desc';
export function LoanTable({
  loans,
  onMarkComplete,
  onView,
  showActions = true,
  title
}: LoanTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('fundedDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }
  const filteredLoans = loans.filter(loan => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return loan.borrowerName.toLowerCase().includes(q) || loan.loanNumber.includes(q) || loan.loanType.toLowerCase().includes(q) || loan.phone.includes(q);
  });
  const sortedLoans = [...filteredLoans].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'borrowerName':
        return dir * a.borrowerName.localeCompare(b.borrowerName);
      case 'fundedDate':
        {
          const dateA = new Date(a.fundedDate).getTime();
          const dateB = new Date(b.fundedDate).getTime();
          return dir * (dateA - dateB);
        }
      case 'loanType':
        return dir * a.loanType.localeCompare(b.loanType);
      case 'rate':
        {
          const rateA = parseFloat(a.rate.replace('%', ''));
          const rateB = parseFloat(b.rate.replace('%', ''));
          return dir * (rateA - rateB);
        }
      default:
        return 0;
    }
  });
  function SortButton({
    field,
    label
  }: {
    field: SortField;
    label: string;
  }) {
    const isActive = sortField === field;
    return <button onClick={() => handleSort(field)} className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors ${isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} aria-label={`Sort by ${label}`} data-id="element-49">
        {label}
        <ArrowUpDownIcon className="w-3.5 h-3.5" data-id="element-50" />
      </button>;
  }
  return <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-51">
      {(title || true) && <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-52">
          {title && <h3 className="text-sm font-semibold text-gray-900" data-id="element-53">{title}</h3>}
          <div className="relative" data-id="element-54">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-55" />
            <input type="text" placeholder="Search loans..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full sm:w-64 pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" aria-label="Search loans" data-id="element-56" />
          </div>
        </div>}

      {sortedLoans.length === 0 ? <div className="px-5 py-12 text-center" data-id="element-57">
          <p className="text-sm text-gray-500" data-id="element-58">No loans found</p>
        </div> : <div className="overflow-x-auto" data-id="element-59">
          <table className="w-full" role="table" data-id="element-60">
            <thead data-id="element-61">
              <tr className="bg-gray-50/80" data-id="element-62">
                <th className="text-left py-3 px-4" data-id="element-63">
                  <SortButton field="borrowerName" label="Borrower" data-id="element-64" />
                </th>
                <th className="text-left py-3 px-4" data-id="element-65">
                  <SortButton field="fundedDate" label="Funded Date" data-id="element-66" />
                </th>
                <th className="text-left py-3 px-4" data-id="element-67">
                  <SortButton field="loanType" label="Type" data-id="element-68" />
                </th>
                <th className="text-left py-3 px-4" data-id="element-69">
                  <SortButton field="rate" label="Rate" data-id="element-70" />
                </th>
                <th className="text-left py-3 px-4" data-id="element-71">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-72">
                    Phone
                  </span>
                </th>
                <th className="text-left py-3 px-4" data-id="element-73">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-74">
                    Actions
                  </span>
                </th>
              </tr>
            </thead>
            <tbody data-id="element-75">
              {sortedLoans.map(loan => <LoanRow key={loan.id} loan={loan} onMarkComplete={onMarkComplete} onView={onView} showActions={showActions} data-id="element-76" />)}
            </tbody>
          </table>
        </div>}

      <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50" data-id="element-77">
        <p className="text-xs text-gray-500" data-id="element-78">
          Showing {sortedLoans.length} of {loans.length} loans
        </p>
      </div>
    </div>;
}